import { PublicClientApplication, Configuration, AuthenticationResult, BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { getConfig, loadConfig } from '../config';
import { isMsalCacheKey, INTENTIONAL_LOGOUT_KEY } from './msalCacheKeys';
import { getGoogleIdentity } from './googleIdentity';
import {
  User,
  AuthProvider,
} from '../types/auth';

/**
 * Provedor com que a sessão atual foi aberta (Fase 8). Fica em localStorage para sobreviver ao
 * reload: decide se um 401 do /auth/me tenta a renovação silenciosa pelo MSAL (Entra) ou se volta
 * direto ao login (Google — a sessão é emitida pelo app, cookie de 30 dias, e não há o que renovar).
 */
export const PROVIDER_KEY = 'plan_provider';

export const getStoredProvider = (): AuthProvider | null => {
  try {
    const v = localStorage.getItem(PROVIDER_KEY);
    return v === 'google' || v === 'entra' ? v : null;
  } catch {
    return null;
  }
};

const storeProvider = (provider: AuthProvider | null): void => {
  try {
    if (provider) localStorage.setItem(PROVIDER_KEY, provider);
    else localStorage.removeItem(PROVIDER_KEY);
  } catch { /* ignore */ }
};

/** Sessão aberta com o Google: nada de MSAL (nem renovação silenciosa, nem logout da Microsoft). */
const isGoogleSession = (): boolean => getStoredProvider() === 'google';

// Interface para troca de tokens com backend (Entra ID)
interface ExchangeTokenRequest {
  provider?: 'entra';
  accessToken: string;
  idToken?: string;
  account: {
    homeAccountId: string;
    name: string;
    username: string;
    tenantId: string;
  };
  deviceInfo: {
    userAgent: string;
    timestamp: number;
  };
}

// Interface para resposta do /auth/me
interface AuthMeUser {
  oid: string;
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  tid: string;
  roles: string[];
  /** Fase 8: `entra` | `google` (do cadastro da sessão). */
  provider?: string;
}

interface AuthMeResponse {
  success: boolean;
  user?: AuthMeUser;
  data?: AuthMeUser;
  token?: string;
}

class AuthService {
  private currentUser: User | null = null;
  private msalInstance: PublicClientApplication | null = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private loginInProgress = false;
  private authCheckPromise: Promise<User | null> | null = null;
  private isRedirectingToLogin = false;

  constructor() {
    // Configuration will be loaded lazily when needed
  }

  /**
   * Ensure configuration is loaded
   */
  private async ensureConfigLoaded(): Promise<void> {
    try {
      getConfig();
    } catch {
      await loadConfig();
    }
  }

  /**
   * Get base URL from configuration
   */
  private async getBaseUrl(): Promise<string> {
    await this.ensureConfigLoaded();
    return getConfig().api.baseUrl;
  }

  /**
   * Debug JWT token content
   */
  // ...existing code...

  /**
   * Process successful login response
   */
  private async processLoginResponse(response: AuthenticationResult): Promise<User> {
    if (!response.account) {
      throw new Error('No account information returned from Azure AD');
    }

    console.debug('🔄 Processing login response...');
    console.debug('📊 Account info:', {
      localAccountId: response.account.localAccountId,
      name: response.account.name,
      username: response.account.username
    });

    // Extract user information
    const extractedRoles = this.extractRolesFromToken(response);
    const extractedRole = this.extractRoleFromToken(response);
    const extractedPermissions = this.extractPermissionsFromToken(response);
    
    console.debug('🎭 Extracted from token:', {
      roles: extractedRoles,
      primaryRole: extractedRole,
      permissions: extractedPermissions
    });

    const user: User = {
      id: response.account.localAccountId,
      name: response.account.name || response.account.username,
      email: response.account.username,
      role: extractedRole,
      roles: extractedRoles,
      permissions: extractedPermissions,
      provider: 'entra',
    };
    storeProvider('entra');

    console.debug('👤 Usuário MSAL criado:', {
      email: user.email,
      role: user.role,
      roles: user.roles,
      permissions: user.permissions
    });

    this.currentUser = user;
    return user;
  }

  /**
   * Initialize MSAL configuration with Azure AD
   */
  private async initializeMsal(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.isInitialized && this.msalInstance) {
      return;
    }

    this.initializationPromise = (async () => {
      try {
        await this.ensureConfigLoaded();
        const config = getConfig();
        
        const msalConfig: Configuration = {
          auth: {
            clientId: config.auth.azureAd.clientId,
            authority: `https://login.microsoftonline.com/${config.auth.azureAd.tenantId}`,
            redirectUri: config.auth.azureAd.redirectUri,
            postLogoutRedirectUri: window.location.origin,
          },
          cache: {
            // localStorage persiste a conta/refresh token MSAL através do round-trip do popup
            // e de reloads (sessionStorage é por aba e é limpo agressivamente, o que fazia o
            // segundo passo do login não encontrar a conta e re-exibir o seletor de contas).
            cacheLocation: 'localStorage',
            // Mantém o estado de autenticação também em cookie, necessário em navegadores
            // com políticas restritas de armazenamento (Safari/iTP, webviews mobile) durante
            // o hop do popup — evita perda de estado entre o seletor de contas e o callback.
            storeAuthStateInCookie: true,
          },
          system: {
            loggerOptions: {
              loggerCallback: () => {},
              piiLoggingEnabled: false,
              logLevel: 3, // Warning level
            },
            allowNativeBroker: false,
          }
        };

        this.msalInstance = new PublicClientApplication(msalConfig);
        await this.msalInstance.initialize();

        // Process redirect promise
        const response = await this.msalInstance.handleRedirectPromise();
        if (response) {
          await this.processLoginResponse(response);
        }

        this.isInitialized = true;
        
      } catch (error) {
        console.error('Error initializing MSAL:', error);
        this.isInitialized = false;
        this.msalInstance = null;
        this.initializationPromise = null;
        throw error;
      }
    })();

    return this.initializationPromise;
  }

  /**
   * Login using Azure AD
   */
  async login(): Promise<User> {
    if (this.loginInProgress) {
      throw new Error('Login já está em andamento. Aguarde alguns segundos.');
    }

    try {
      this.loginInProgress = true;

      // Um login explícito é o único evento que reabre a renovação silenciosa (ver logout()).
      this.clearIntentionalLogout();

      await this.initializeMsal();

      if (!this.msalInstance) {
        throw new Error('MSAL não foi inicializado corretamente');
      }

      const config = getConfig();
      
      // Check for existing accounts
      const accounts = this.msalInstance.getAllAccounts();

      // `prompt: 'select_account'` só faz sentido em chamadas interativas (popup/redirect).
      // Passá-lo no acquireTokenSilent é ignorado e conceitualmente errado; mantê-lo no
      // request silencioso ajudava a forçar o seletor de contas indevidamente.
      const silentRequest = {
        scopes: config.auth.azureAd.scopes,
      };
      const interactiveRequest = {
        scopes: config.auth.azureAd.scopes,
        prompt: 'select_account',
      };

      let response: AuthenticationResult;

      try {
        if (accounts && accounts.length > 0) {
          // Try silent login first
          try {
            response = await this.msalInstance.acquireTokenSilent({
              ...silentRequest,
              account: accounts[0]
            });
          } catch (silentError) {

            // Try popup first, then redirect as fallback
            try {
              response = await this.msalInstance.acquireTokenPopup(interactiveRequest);
            } catch (popupError) {

              // Use redirect as ultimate fallback
              await this.msalInstance.acquireTokenRedirect(interactiveRequest);
              throw new Error('Redirecionando para autenticação Microsoft...');
            }
          }
        } else {
          // No accounts, try popup first
          try {
            response = await this.msalInstance.acquireTokenPopup(interactiveRequest);
          } catch (popupError) {

            // Use redirect as fallback
            await this.msalInstance.acquireTokenRedirect(interactiveRequest);
            throw new Error('Redirecionando para autenticação Microsoft...');
          }
        }

        // Process successful login
        const user = await this.processLoginResponse(response);
        
        // Troca token Azure AD por cookie HTTP-Only no backend (opcional)
        try {
          await this.exchangeTokenForCookie(response);
        } catch (exchangeError: unknown) {
          console.warn('Token exchange failed, but login succeeded:', exchangeError);
          // Não falha o login se o exchange falhar - o usuário ainda pode usar a aplicação
          // com tokens MSAL se o backend não estiver disponível
        }

        // NÃO revalidar o usuário sincronamente via cookie (GET /auth/me) aqui.
        //
        // O código anterior limpava `currentUser` e chamava getCurrentUser(true)
        // imediatamente após o exchange. Em produção, o Set-Cookie de sessão
        // (SameSite/Secure) ainda não estava disponível para esse XHR imediato,
        // o /auth/me retornava 401 e o usuário era jogado de volta para a tela de
        // login — gerando o segundo seletor de contas da Microsoft (duplo login).
        //
        // O cookie continua sendo gravado para as próximas navegações; basta
        // retornar o usuário recém-autenticado pelo MSAL.
        this.currentUser = user;
        return user;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (msalError: any) {
        console.error('Error during MSAL login:', msalError);
        
        // Don't fallback to mock on redirect
        if (msalError.message?.includes('Redirecionando')) {
          throw msalError;
        }

        // Better error messages
        if (msalError instanceof BrowserAuthError) {
          throw new Error(`Erro de autenticação: ${msalError.errorCode} - ${msalError.errorMessage}`);
        } else if (msalError.message?.includes('popup_window_error')) {
          throw new Error('Popup bloqueado. Permita popups para este site e tente novamente.');
        } else {
          throw new Error(`Falha na autenticação: ${msalError.message || 'Erro desconhecido'}`);
        }
      }

    } finally {
      this.loginInProgress = false;
    }
  }

  /**
   * Extract main role from Azure AD token
   */
  private extractRoleFromToken(authResult: AuthenticationResult): string {
    try {
      const roles = this.extractRolesFromToken(authResult);
      if (roles && roles.length > 0) {
        return roles[0];
      }
    } catch (error) {
      console.error('Error extracting role from token:', error);
    }
    
    return 'user';
  }

  /**
   * Extract roles array from Azure AD token
   */
  private extractRolesFromToken(authResult: AuthenticationResult): string[] {
    console.debug('🔍 Extracting roles from token...');
    
    try {
      if (authResult.idTokenClaims) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const claims = authResult.idTokenClaims as any;
        console.debug('📄 ID Token Claims:', JSON.stringify(claims, null, 2));
        
        // Try different claims where roles might be stored
        let roles: string[] = [];
        
        // 1. Standard 'roles' claim
        if (claims.roles && Array.isArray(claims.roles)) {
          console.debug('✅ Found roles in standard "roles" claim:', claims.roles);
          roles = claims.roles;
        }
        // 2. Microsoft custom claim (full format)
        else if (claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
          const rolesClaim = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
          console.debug('✅ Found roles in Microsoft claim:', rolesClaim);
          roles = Array.isArray(rolesClaim) ? rolesClaim : [rolesClaim];
        }
        // 3. Custom extension_Role claim
        else if (claims.extension_Role) {
          console.debug('✅ Found roles in extension_Role claim:', claims.extension_Role);
          roles = Array.isArray(claims.extension_Role) ? claims.extension_Role : [claims.extension_Role];
        }
        // 4. Check for other possible role claims
        else if (claims.role) {
          console.debug('✅ Found roles in "role" claim:', claims.role);
          roles = Array.isArray(claims.role) ? claims.role : [claims.role];
        }
        // 5. Check for app roles
        else if (claims.app_roles) {
          console.debug('✅ Found roles in "app_roles" claim:', claims.app_roles);
          roles = Array.isArray(claims.app_roles) ? claims.app_roles : [claims.app_roles];
        }
        // 6. Try accessToken if available
        else if (authResult.accessToken) {
          console.debug('🔄 Trying access token for roles...');
          roles = this.extractRolesFromAccessToken(authResult.accessToken);
        }
        else {
          console.debug('⚠️ No role claims found, checking all claims for role-like values...');
          // Log all claims to see what's available
          Object.keys(claims).forEach(key => {
            if (key.toLowerCase().includes('role') || key.toLowerCase().includes('group')) {
              console.debug(`🔍 Found potential role claim "${key}":`, claims[key]);
            }
          });
        }
        
        if (roles && roles.length > 0) {
          // Filter valid roles
          const validRoles = roles.filter(role => role && typeof role === 'string' && role.trim().length > 0);
          console.debug('✅ Final extracted roles:', validRoles);
          return validRoles;
        } else {
          console.debug('❌ No valid roles found');
        }
      } else {
        console.debug('❌ No idTokenClaims available');
        
        // Try access token as fallback
        if (authResult.accessToken) {
          console.debug('🔄 Trying access token as fallback...');
          const accessTokenRoles = this.extractRolesFromAccessToken(authResult.accessToken);
          if (accessTokenRoles.length > 0) {
            console.debug('✅ Found roles in access token:', accessTokenRoles);
            return accessTokenRoles;
          }
        }
      }
    } catch (error) {
      console.error('❌ Error extracting roles from token:', error);
    }
    
    console.debug('❌ No roles found, returning empty array');
    return [];
  }

  /**
   * Extract roles from access token JWT
   */
  private extractRolesFromAccessToken(accessToken: string): string[] {
    console.debug('🔍 Extracting roles from access token...');
    
    try {
      const payload = this.decodeJwtPayload(accessToken);
      console.debug('📄 Access Token Payload:', JSON.stringify(payload, null, 2));
      
      // Try different claims in access token
      let roles: string[] = [];
      
      if (payload.roles && Array.isArray(payload.roles)) {
        console.debug('✅ Found roles in access token "roles" claim:', payload.roles);
        roles = payload.roles;
      }
      else if (payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
        const rolesClaim = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        console.debug('✅ Found roles in access token Microsoft claim:', rolesClaim);
        roles = Array.isArray(rolesClaim) ? rolesClaim : [rolesClaim];
      }
      else if (payload.role) {
        console.debug('✅ Found roles in access token "role" claim:', payload.role);
        roles = Array.isArray(payload.role) ? payload.role : [payload.role];
      }
      else if (payload.app_roles) {
        console.debug('✅ Found roles in access token "app_roles" claim:', payload.app_roles);
        roles = Array.isArray(payload.app_roles) ? payload.app_roles : [payload.app_roles];
      }
      else {
        console.debug('⚠️ No role claims found in access token, checking all claims...');
        Object.keys(payload).forEach(key => {
          if (key.toLowerCase().includes('role') || key.toLowerCase().includes('group')) {
            console.debug(`🔍 Found potential role claim "${key}" in access token:`, payload[key]);
          }
        });
      }
      
      const validRoles = roles.filter(role => role && typeof role === 'string' && role.trim().length > 0);
      console.debug('✅ Valid roles from access token:', validRoles);
      return validRoles;
      
    } catch (error) {
      console.error('❌ Error extracting roles from accessToken:', error);
      return [];
    }
  }

  /**
   * Decode JWT payload
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private decodeJwtPayload(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token - does not have 3 parts');
    }

    const payload = parts[1];
    // Fix padding if necessary
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }

  /**
   * Extract permissions from Azure AD token
   */
  private extractPermissionsFromToken(authResult: AuthenticationResult): string[] {
    // Use role-based permissions instead of token scopes
    const roles = this.extractRolesFromToken(authResult);
    return this.mapRolesToPermissions(roles.length > 0 ? roles : ['user']);
  }



  /**
   * Logout user from Azure AD and local session
   */
  /**
   * Exchange Azure AD token for HTTP-Only cookie
   */
  private async exchangeTokenForCookie(response: AuthenticationResult): Promise<void> {
    if (!response.account) {
      throw new Error('No account information available for token exchange');
    }

    const baseUrl = await this.getBaseUrl();
    const exchangeRequest: ExchangeTokenRequest = {
      provider: 'entra',
      accessToken: response.accessToken,
      idToken: response.idToken || '',
      account: {
        homeAccountId: response.account.homeAccountId,
        name: response.account.name || response.account.username,
        username: response.account.username,
        tenantId: response.tenantId || ''
      },
      deviceInfo: {
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      }
    };

    try {
      const exchangeResponse = await fetch(`${baseUrl}/auth/exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Importante para receber o cookie
        body: JSON.stringify(exchangeRequest)
      });

      if (!exchangeResponse.ok) {
        if (exchangeResponse.status === 404) {
          throw new Error('Auth exchange endpoint not available. Please check backend configuration.');
        }
        throw new Error(`Token exchange failed with status ${exchangeResponse.status}`);
      }

      const data = await exchangeResponse.json();
      if (!data.success) {
        throw new Error('Token exchange was not successful');
      }
    } catch (error) {
      console.error('Error exchanging token for cookie:', error);
      throw error;
    }
  }

  /**
   * Login com Google (Fase 8): troca o ID token do Google Identity Services pelo cookie de sessão
   * (`POST /auth/exchange { provider: 'google', idToken }`) e monta o `User` a partir do `/auth/me`.
   * No primeiro login o backend cria o cadastro (plano `trial`); com o cadastro fechado responde
   * 403 `signup_disabled`.
   */
  async loginWithGoogle(idToken: string): Promise<User> {
    if (this.loginInProgress) {
      throw new Error('Login já está em andamento. Aguarde alguns segundos.');
    }
    if (!idToken) {
      throw new Error('O Google não devolveu a credencial de login. Tente novamente.');
    }

    try {
      this.loginInProgress = true;
      this.clearIntentionalLogout();

      const baseUrl = await this.getBaseUrl();
      const exchangeResponse = await fetch(`${baseUrl}/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          provider: 'google',
          idToken,
          deviceInfo: { userAgent: navigator.userAgent, timestamp: Date.now() },
        }),
      });

      const body = await exchangeResponse.json().catch(() => ({} as Record<string, unknown>));
      if (!exchangeResponse.ok || !body?.success) {
        const code = typeof body?.code === 'string' ? body.code : typeof body?.error === 'string' ? body.error : '';
        const message = typeof body?.message === 'string' && body.message ? body.message : '';
        if (exchangeResponse.status === 403 && code === 'signup_disabled') {
          throw new Error('O cadastro de novos usuários está fechado no momento.');
        }
        if (exchangeResponse.status === 403) {
          throw new Error(message || 'Acesso negado.');
        }
        if (exchangeResponse.status === 401) {
          throw new Error('O Google não confirmou a sua conta. Tente entrar novamente.');
        }
        if (exchangeResponse.status === 400) {
          throw new Error(message || 'Login com Google indisponível neste servidor.');
        }
        throw new Error(`Falha no login com Google (${exchangeResponse.status}).`);
      }

      storeProvider('google');

      // Monta o usuário pelo /auth/me (fonte do papel). Se o cookie recém-gravado ainda não chegar
      // neste XHR (ver comentário em login()), cai no `user` que o próprio exchange devolveu.
      let user: User | null = null;
      try {
        const meResponse = await fetch(`${baseUrl}/auth/me`, { method: 'GET', credentials: 'include' });
        if (meResponse.ok) user = this.parseAuthMeResponse(await meResponse.json());
      } catch (meError) {
        console.warn('⚠️ /auth/me falhou logo após o exchange do Google:', meError);
      }
      if (!user) user = this.parseAuthMeResponse(body as AuthMeResponse);
      if (!user) throw new Error('Não foi possível carregar o usuário após o login com Google.');

      user.provider = 'google';
      this.currentUser = user;
      return user;
    } finally {
      this.loginInProgress = false;
    }
  }

  async logout(): Promise<void> {
    console.log('🚪 Iniciando logout completo...');

    // 1. Marca o logout como INTENCIONAL antes de qualquer coisa.
    //    Sem essa marca, o 401 do /auth/me no boot seguinte dispara a renovação silenciosa
    //    (trysilentTokenRenewal → acquireTokenSilent → /auth/exchange) e o usuário volta logado
    //    sozinho — era metade do motivo de o botão "Sair" não funcionar. Fica em localStorage
    //    porque precisa sobreviver ao round-trip até o endpoint de logout da Microsoft.
    this.markIntentionalLogout();

    // 2. Notificar backend para invalidar/remover o cookie HTTP-Only
    //    O backend DEVE responder com Set-Cookie: <nome>=; Max-Age=0 para apagar o cookie
    await this.logoutFromBackend();

    // 2b. Sessão aberta com o Google (Fase 8): não existe sessão da Microsoft para encerrar.
    //     Desliga a seleção automática do Google (para o próximo "Entrar" pedir a conta), limpa o
    //     estado local e volta ao login.
    if (isGoogleSession()) {
      try {
        getGoogleIdentity()?.disableAutoSelect();
      } catch (error) {
        console.warn('⚠️ google.accounts.id.disableAutoSelect falhou:', error);
      }
      storeProvider(null);
      await this.clearAll();
      console.log('✅ Logout (Google) concluído - redirecionando para login');
      window.location.replace('/login');
      return;
    }

    storeProvider(null);

    // 3. Encerrar também a sessão no Entra ID. Um logout só local deixa a sessão da Microsoft viva:
    //    o SSO reemite o token na hora e o próximo "Entrar" nem pede a conta. O logoutRedirect
    //    limpa o cache do MSAL e navega para o endpoint de logout, que devolve o usuário a /login.
    try {
      await this.initializeMsal();
      if (this.msalInstance) {
        const account = this.msalInstance.getAllAccounts()[0];
        await this.msalInstance.logoutRedirect({
          account,
          postLogoutRedirectUri: `${window.location.origin}/login`,
        });
        return; // navegação em curso — nada depois disto executa
      }
    } catch (error) {
      console.warn('⚠️ logoutRedirect falhou — seguindo com logout local:', error);
    }

    // 4. Fallback (MSAL indisponível ou logoutRedirect recusado): limpeza manual + hard-redirect,
    //    para o botão nunca ficar inerte. O reload completo descarta o estado React/módulo em
    //    memória, incluindo o cache de permissões.
    await this.clearAll();
    console.log('✅ Logout concluído - redirecionando para login');
    window.location.replace('/login');
  }

  /** Registra que o usuário pediu para sair (ver logout/clearIntentionalLogout). */
  private markIntentionalLogout(): void {
    try {
      localStorage.setItem(INTENTIONAL_LOGOUT_KEY, '1');
    } catch { /* ignore */ }
  }

  /** O último logout foi um clique em "Sair"? Se sim, não reautenticar em silêncio. */
  private wasIntentionalLogout(): boolean {
    try {
      return localStorage.getItem(INTENTIONAL_LOGOUT_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Só um login explícito reabre a porta da renovação silenciosa. */
  private clearIntentionalLogout(): void {
    try {
      localStorage.removeItem(INTENTIONAL_LOGOUT_KEY);
    } catch { /* ignore */ }
  }

  /**
   * Logout from backend to clear HTTP-Only cookie.
   *
   * IMPORTANTE — o backend deve responder com o header:
   *   Set-Cookie: <nome_do_cookie>=; Path=/; Max-Age=0; HttpOnly; SameSite=Lax
   * Sem isso o browser NÃO consegue remover um cookie HttpOnly.
   */
  private async logoutFromBackend(): Promise<void> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include' // Obrigatório para enviar e receber o cookie
      });

      if (!response.ok) {
        console.warn(`⚠️ Backend logout respondeu com status ${response.status} — cookie pode não ter sido removido.`);
      } else {
        console.log('✅ Backend logout bem-sucedido (cookie removido pelo servidor)');
      }
    } catch (error) {
      // Não bloqueia o logout local mesmo se o backend estiver indisponível
      console.warn('⚠️ Falha ao contatar backend no logout (continuando limpeza local):', error);
    }
  }

  /**
   * Parse /auth/me response and return User or null
   */
  private parseAuthMeResponse(data: AuthMeResponse): User | null {
    console.log('📄 Dados recebidos do backend (raw):', JSON.stringify(data, null, 2));
    
    const userData = data.user || data.data;
    
    if (data && data.success === true && userData) {
      console.log('✅ Usuário autenticado via cookie:', {
        email: userData.email,
        roles: userData.roles
      });
      
      const userRoles = Array.isArray(userData.roles) ? userData.roles : [];
      const primaryRole = userRoles.length > 0 ? userRoles[0] : 'user';
      const userPermissions = this.mapRolesToPermissions(userRoles.length > 0 ? userRoles : ['user']);
      const provider: AuthProvider | undefined =
        userData.provider === 'google' || userData.provider === 'entra' ? userData.provider : getStoredProvider() ?? undefined;
      
      const user: User = {
        id: userData.oid || userData.email,
        name: userData.name || userData.given_name || userData.email,
        email: userData.email,
        role: primaryRole,
        roles: userRoles,
        permissions: userPermissions,
        provider,
      };
      // O backend é a fonte: se a sessão diz "google", o MSAL não entra mais na história.
      if (userData.provider === 'google' || userData.provider === 'entra') storeProvider(userData.provider);
      
      console.log('💾 Salvando usuário no cache e retornando...');
      this.currentUser = user;
      return user;
    }
    
    console.log('❌ Resposta inválida do backend:', {
      success: data?.success,
      hasUser: !!data?.user,
      hasDataField: !!data?.data
    });
    return null;
  }

  /**
   * Tenta renovação silenciosa do token via MSAL + exchange + retry /auth/me
   * Chamado quando /auth/me retorna 401 (cookie expirado)
   */
  private async trysilentTokenRenewal(authMeUrl: string): Promise<User | null> {
    try {
      // Garantir que MSAL está inicializado
      await this.initializeMsal();
      if (!this.msalInstance) return null;

      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.log('🚫 Nenhuma conta MSAL encontrada - renovação silenciosa impossível');
        return null;
      }

      const config = getConfig();
      const silentRequest = {
        scopes: config.auth.azureAd.scopes,
        account: accounts[0],
        forceRefresh: false
      };

      // 1ª tentativa: acquireTokenSilent com cache
      let tokenResponse: AuthenticationResult;
      try {
        tokenResponse = await this.msalInstance.acquireTokenSilent(silentRequest);
        console.log('✅ Token silencioso obtido do cache MSAL');
      } catch (silentError) {
        // 2ª tentativa: forçar refresh do token
        console.log('🔄 Cache MSAL falhou, forçando refresh...');
        try {
          tokenResponse = await this.msalInstance.acquireTokenSilent({
            ...silentRequest,
            forceRefresh: true
          });
          console.log('✅ Token renovado via refresh silencioso');
        } catch (refreshError) {
          console.warn('❌ Renovação silenciosa falhou:', refreshError);
          return null;
        }
      }

      // Trocar o token renovado por um novo cookie HTTP-Only
      await this.exchangeTokenForCookie(tokenResponse);
      console.log('✅ Cookie renovado via exchange');

      // Retry /auth/me com o novo cookie
      const retryResponse = await fetch(authMeUrl, {
        method: 'GET',
        credentials: 'include'
      });

      if (retryResponse.ok) {
        const retryData: AuthMeResponse = await retryResponse.json();
        const user = this.parseAuthMeResponse(retryData);
        if (user) {
          console.log('✅ Renovação silenciosa bem-sucedida - usuário autenticado sem interação');
          return user;
        }
      }

      console.warn('⚠️ /auth/me ainda falhou após renovação silenciosa');
      return null;
    } catch (error) {
      console.warn('❌ Erro na renovação silenciosa:', error);
      return null;
    }
  }

  /**
   * Get current authenticated user
   */
  /**
   * Get current user with optional force refresh
   */
  async getCurrentUser(forceRefresh: boolean = false): Promise<User | null> {
    console.log('🔍 getCurrentUser chamado - forceRefresh:', forceRefresh);
    
    // Se forceRefresh foi solicitado, limpar cache
    if (forceRefresh) {
      this.currentUser = null;
      console.log('🔄 Cache limpo - verificação forçada');
    }

    // Primeiro verifica se temos usuário em cache
    if (this.currentUser) {
      console.log('✅ Usuário em cache encontrado:', this.currentUser.email);
      return this.currentUser;
    }

    // Previne múltiplas chamadas simultâneas usando promise compartilhada
    if (this.authCheckPromise) {
      console.log('⏳ Verificação já em andamento - reutilizando promise...');
      return this.authCheckPromise;
    }

    this.authCheckPromise = this._doGetCurrentUser();
    return this.authCheckPromise;
  }

  private async _doGetCurrentUser(): Promise<User | null> {
    try {
      // ÚNICO MÉTODO: Verificar autenticação via cookie HTTP-Only (/auth/me)
      console.log('🔍 Verificando autenticação via cookie HTTP-Only...');
      const baseUrl = await this.getBaseUrl();
      const authMeUrl = `${baseUrl}/auth/me`;
      
      console.log('🌐 Fazendo requisição para:', authMeUrl);
      console.log('🍪 Usando credentials: include para envio de cookies');
      
      const response = await fetch(authMeUrl, {
        method: 'GET',
        credentials: 'include' // OBRIGATÓRIO para cookies
      });

      console.log('🌐 /auth/me response completa:', {
        url: authMeUrl,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        console.log('✅ Response OK - processando dados...');
        const user = this.parseAuthMeResponse(await response.json());
        if (user) return user;
      } else if (response.status === 401) {
        // Depois de um "Sair" o 401 é definitivo: renovar em silêncio aqui é exatamente o que
        // trazia o usuário de volta para dentro do sistema logo após o logout.
        if (this.wasIntentionalLogout()) {
          console.log('🚫 401 após logout intencional - não renovando sessão');
          return null;
        }

        // Sessão do Google (Fase 8): não há token da Microsoft para renovar — 401 = volta ao login.
        if (isGoogleSession()) {
          console.log('🚫 Sessão do Google expirada (401) - sem renovação silenciosa');
          return null;
        }

        console.log('🚫 Cookie expirado (401) - tentando renovação silenciosa via MSAL...');

        // Tentar renovação silenciosa: acquireTokenSilent → exchangeTokenForCookie → retry /auth/me
        const renewedUser = await this.trysilentTokenRenewal(authMeUrl);
        if (renewedUser) return renewedUser;

        console.log('🚫 Renovação silenciosa falhou - usuário não autenticado');
      } else {
        console.log(`⚠️ Status inesperado do servidor: ${response.status} - ${response.statusText}`);
        try {
          const responseText = await response.text();
          console.log('📄 Resposta do servidor para status inesperado:', responseText);
        } catch (textError) {
          console.debug('⚠️ Não foi possível ler resposta do servidor');
        }
      }
    } catch (error) {
      console.error('❌ Erro na verificação via cookie (detalhes):', {
        message: error instanceof Error ? error.message : 'Erro desconhecido',
        stack: error instanceof Error ? error.stack : undefined,
        type: typeof error,
        error: error
      });
    } finally {
      this.authCheckPromise = null;
      console.log('🏁 getCurrentUser: Finalizando verificação de autenticação');
    }

    // Se chegou aqui, usuário NÃO está autenticado
    console.log('❌ getCurrentUser: Retornando NULL - usuário não autenticado');
    return null;
  }

  /**
   * Force check authentication status (bypasses cache)
   * Usado para verificar se há cookie válido após login
   */
  async checkAuthenticationStatus(): Promise<User | null> {
    return this.getCurrentUser(true);
  }

  /**
   * Renova proativamente o cookie de sessão usando o token MSAL.
   * Chamado periodicamente para evitar que o cookie expire durante o uso.
   */
  async refreshSessionCookie(): Promise<void> {
    try {
      // O efeito de refresh proativo do AuthContext (a cada 4 min, e em focus/visibilitychange)
      // continua montado durante o logout — sem esta guarda ele pode recriar o cookie entre o
      // POST /auth/logout e o redirect, e o usuário nunca chega a sair.
      if (this.wasIntentionalLogout()) return;

      // Sessão do Google: o cookie é emitido pelo app (30 dias); não há token MSAL a trocar.
      if (isGoogleSession()) return;

      await this.initializeMsal();
      if (!this.msalInstance) return;

      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) return;

      const config = getConfig();
      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: config.auth.azureAd.scopes,
        account: accounts[0],
        forceRefresh: false // MSAL renova automaticamente se perto de expirar
      });

      await this.exchangeTokenForCookie(tokenResponse);
      console.debug('✅ Proactive refresh: cookie de sessão renovado');
    } catch (error) {
      console.warn('⚠️ Proactive refresh falhou:', error);
    }
  }

  /**
   * Map user roles to permissions (helper method)
   */
  private mapRolesToPermissions(roles: string[]): string[] {
    console.debug('🔧 Mapping roles to permissions:', { inputRoles: roles });

    const rolePermissionMap: Record<string, string[]> = {
      // Admin roles (full access)
      admin: ['*'],
      administrator: ['*'],

      // Demais perfis (marketing_analyst, ...) NÃO ficam aqui: a fonte autoritativa é
      // /security/permission-config.json, consultada pelo usePermissions. Este mapa é só um
      // atalho positivo (ver CLAUDE.md, "Authorization").
      user: []
    };

    const permissions = new Set<string>();
    roles.forEach(role => {
      const rolePermissions = rolePermissionMap[role] || [];
      console.debug(`📝 Role '${role}' → permissions:`, rolePermissions);
      rolePermissions.forEach(permission => permissions.add(permission));
    });

    const finalPermissions = Array.from(permissions);
    console.debug('✅ Final mapped permissions:', finalPermissions);
    return finalPermissions;
  }

  /**
   * Check if user is currently authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    // Check local state first
    if (this.currentUser) return true;

    // Verifica via backend se há cookie válido
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/auth/me`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data: AuthMeResponse = await response.json();
        return data.success && !!data.user;
      }
    } catch (error) {
      // Silent error
    }

    // Sessão do Google sem cookie válido = não autenticado (não há conta MSAL a consultar).
    if (isGoogleSession()) return false;

    // Verifica se há conta MSAL (não faz login)
    try {
      if (!this.msalInstance || !this.isInitialized) {
        await this.initializeMsal();
      }

      if (this.msalInstance) {
        const accounts = this.msalInstance.getAllAccounts();
        return accounts && accounts.length > 0;
      }
    } catch (error) {
      // Silent error
    }

    return false;
  }

  // ...existing code...

  /**
   * Get authentication token for API requests
   * @param forceRefresh - se true, ignora o cache e obtém um novo token do servidor
   */
  async getAuthToken(forceRefresh = false): Promise<string | null> {
    if (!this.msalInstance || !this.isInitialized) {
      await this.initializeMsal();
    }
    
    if (!this.msalInstance) {
      return null;
    }

    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }

      const config = getConfig();
      const silentRequest = {
        scopes: config.auth.azureAd.scopes,
        account: accounts[0],
        forceRefresh
      };
      
      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Força o refresh do token e atualiza o cookie de sessão no backend
   */
  private async forceTokenRefreshAndExchange(): Promise<string | null> {
    if (!this.msalInstance) return null;

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const config = getConfig();
    const silentRequest = {
      scopes: config.auth.azureAd.scopes,
      account: accounts[0],
      forceRefresh: true
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(silentRequest);

      // Atualiza o cookie de sessão no backend com o token renovado
      try {
        await this.exchangeTokenForCookie(response);
        console.debug('✅ Cookie de sessão renovado com novo token');
      } catch (exchangeError) {
        console.warn('⚠️ Falha ao renovar cookie, mas token foi obtido:', exchangeError);
      }

      return response.accessToken;
    } catch (error) {
      // Refresh token também expirado: user precisa re-autenticar via popup
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('🔐 Refresh token expirado, solicitando re-autenticação via popup...');
        try {
          const response = await this.msalInstance.acquireTokenPopup({
            scopes: config.auth.azureAd.scopes,
            account: accounts[0]
          });

          try {
            await this.exchangeTokenForCookie(response);
          } catch (exchangeError) {
            console.warn('⚠️ Falha ao renovar cookie após popup:', exchangeError);
          }

          return response.accessToken;
        } catch (popupError) {
          console.error('❌ Falha no popup de re-autenticação:', popupError);
          return null;
        }
      }

      console.error('❌ Falha ao forçar refresh do token:', error);
      return null;
    }
  }

  /**
   * Garante uma sessão válida ANTES de uma operação crítica (ex.: confirmar entrega).
   *
   * Faz o refresh forçado do access token via MSAL (refresh token) e re-grava o
   * cookie de sessão no backend. Use isto logo antes de submeter um formulário
   * sensível para evitar o 401 causado por token expirado enquanto o app esteve
   * em segundo plano (tela do celular em repouso durante a navegação no Waze).
   *
   * Retorna o access token renovado, ou `null` se nem o refresh token for válido
   * (nesse caso o chamador deve preservar os dados — ver deliveryDraftStore).
   */
  async ensureFreshSession(): Promise<string | null> {
    if (!this.msalInstance || !this.isInitialized) {
      await this.initializeMsal();
    }
    return this.forceTokenRefreshAndExchange();
  }

  /**
   * Get API client with authentication (cookie + MSAL token fallback + auto token refresh)
   */
  async getApiClient(): Promise<{ get: (url: string) => Promise<Response>, post: (url: string, data?: unknown) => Promise<Response>, put: (url: string, data?: unknown) => Promise<Response>, patch: (url: string, data?: unknown) => Promise<Response>, delete: (url: string) => Promise<Response> }> {
    const baseUrl = await this.getBaseUrl();
    
    // Função helper para fazer requisição com fallback de autenticação
    const makeRequest = async (url: string, options: RequestInit): Promise<Response> => {
      // Multipart (FormData): o navegador define o Content-Type com o boundary — nunca manual.
      const isMultipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
      const buildHeaders = (token?: string) => ({
        ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(options.headers || {})
      });

      // 1ª tentativa: apenas com cookie de sessão
      let response = await fetch(`${baseUrl}${url}`, {
        ...options,
        credentials: 'include' as RequestCredentials,
        headers: buildHeaders()
      });

      if (response.status !== 401) {
        return response;
      }

      // Sessão do Google (Fase 8): sem token da Microsoft para tentar — 401 é definitivo.
      if (isGoogleSession()) {
        if (!this.isRedirectingToLogin) {
          this.isRedirectingToLogin = true;
          console.warn('🚪 Sessão do Google expirada. Redirecionando para login...');
          this.currentUser = null;
          sessionStorage.clear();
          window.location.href = '/login';
        }
        return response;
      }

      console.debug('🔄 Cookie falhou (401), tentando com token MSAL em cache...');

      // 2ª tentativa: token MSAL do cache
      try {
        const cachedToken = await this.getAuthToken(false);
        if (cachedToken) {
          response = await fetch(`${baseUrl}${url}`, {
            ...options,
            credentials: 'include' as RequestCredentials,
            headers: buildHeaders(cachedToken)
          });

          if (response.status !== 401) {
            if (response.ok) console.debug('✅ Token em cache funcionou');
            return response;
          }
          console.debug('🔄 Token em cache expirado (401), forçando refresh...');
        }
      } catch (tokenError) {
        console.debug('⚠️ Erro ao obter token em cache:', tokenError);
      }

      // 3ª tentativa: forçar refresh do token no Azure AD
      try {
        const freshToken = await this.forceTokenRefreshAndExchange();
        if (freshToken) {
          response = await fetch(`${baseUrl}${url}`, {
            ...options,
            credentials: 'include' as RequestCredentials,
            headers: buildHeaders(freshToken)
          });

          if (response.ok) {
            console.debug('✅ Token renovado com sucesso');
            return response;
          }
        }
      } catch (refreshError) {
        console.debug('❌ Falha no refresh forçado:', refreshError);
      }

      // Se ainda 401 após todas as tentativas, sessão inválida → redireciona para login
      if (response.status === 401) {
        if (!this.isRedirectingToLogin) {
          this.isRedirectingToLogin = true;
          console.warn('🚪 Sessão expirada e não foi possível renovar. Redirecionando para login...');
          this.currentUser = null;
          sessionStorage.clear();
          window.location.href = '/login';
        }
      }

      return response;
    };

    return {
      get: async (url: string) => {
        return makeRequest(url, { method: 'GET' });
      },
      post: async (url: string, data?: unknown) => {
        return makeRequest(url, {
          method: 'POST',
          body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined
        });
      },
      put: async (url: string, data?: unknown) => {
        return makeRequest(url, { 
          method: 'PUT', 
          body: data ? JSON.stringify(data) : undefined
        });
      },
      patch: async (url: string, data?: unknown) => {
        return makeRequest(url, {
          method: 'PATCH',
          body: data ? JSON.stringify(data) : undefined
        });
      },
      delete: async (url: string) => {
        return makeRequest(url, { method: 'DELETE' });
      }
    };
  }

  /**
   * Clear all authentication state (local cache, MSAL, storage)
   */
  async clearAll(): Promise<void> {
    // Reset internal state
    this.currentUser = null;
    this.isInitialized = false;
    this.initializationPromise = null;
    this.loginInProgress = false;
    this.authCheckPromise = null;
    this.isRedirectingToLogin = false;

    // Pede ao próprio MSAL que limpe o cache dele antes de descartar a instância — é a única forma
    // de garantir que conta e refresh token saiam mesmo que o formato das chaves mude de versão.
    const msalInstance = this.msalInstance;

    // Destroy MSAL instance so it is re-created fresh on next login
    this.msalInstance = null;

    if (msalInstance) {
      try {
        await msalInstance.clearCache();
      } catch (error) {
        console.warn('⚠️ msalInstance.clearCache falhou — seguindo com a limpeza manual:', error);
      }
    }

    // Clear sessionStorage (MSAL stores tokens here by default — cacheLocation: 'sessionStorage')
    try {
      sessionStorage.clear();
    } catch { /* ignore */ }

    // Rede de segurança sobre o localStorage. Ver msalCacheKeys.ts: o MSAL v3 NÃO prefixa as
    // entidades com 'msal' — a chave começa com o id da conta e traz '-refreshtoken-' no meio —,
    // então o filtro por prefixo que existia aqui deixava conta e refresh token para trás e o app
    // se reautenticava sozinho depois do "Sair".
    try {
      Object.keys(localStorage)
        .filter(isMsalCacheKey)
        .forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }

    console.log('🧹 clearAll: todos os caches de autenticação limpos');
  }
}

export const authService = new AuthService();
export default authService;